import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { facultyExamApi } from '../../api/examApi';
import { DashboardLayout } from '../../components/layout';
import { parseApiError } from '../../lib/apiErrors';
import { checkPdfFile } from '../../lib/uploadLimits';
import UploadHint from '../../components/UploadHint';

export default function CreateExamPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    duration_minutes: 60,
    total_marks: 100,
    conduct_mode: 'online',
    exam_date: '',
  });
  const offline = formData.conduct_mode === 'offline';

  useEffect(() => {
    facultyExamApi
      .getSubjects()
      .then((res) => setSubjects(res.data))
      .catch(() => toast.error('Failed to load subjects'));
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: ['title', 'subject', 'conduct_mode', 'exam_date'].includes(name) ? value : Number(value) || 0,
    }));
  };

  const handlePdfSelect = (event) => {
    const file = event.target.files?.[0] || null;
    const sizeError = checkPdfFile(file, 'Question paper');
    if (sizeError) {
      toast.error(sizeError);
      event.target.value = '';
      return;
    }
    setPdfFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!offline && !pdfFile) {
      toast.error('Upload the question paper PDF before creating the exam');
      return;
    }
    if (offline && !formData.exam_date) {
      toast.error('Choose the date the paper test is held');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        exam_type: 'subjective_only',
        questions: [],
      };
      if (offline) {
        delete payload.duration_minutes;
      } else {
        delete payload.exam_date;
      }
      const response = await facultyExamApi.createExam(payload);

      if (!offline) {
        const uploadData = new FormData();
        uploadData.append('file', pdfFile);
        await facultyExamApi.uploadPdf(response.data.id, uploadData);
      }

      toast.success('Exam created successfully');
      navigate(`/faculty/exams/${response.data.id}`);
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to create exam').message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Create Exam">
      <div className="max-w-3xl animate-fade-in">
        <button
          onClick={() => navigate('/faculty/exams')}
          className="mb-6 inline-flex items-center gap-2 text-slate-600 hover:text-primary dark:text-slate-400 dark:hover:text-accent-cool"
        >
          <ArrowLeft size={18} />
          Back to Exams
        </button>

        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800 md:p-8">
          <h2 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">{offline ? 'Create Paper Test' : 'Create PDF Exam'}</h2>
          <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
            {offline
              ? 'A paper test is printed and written in class. There is no question file and nothing for students to do online: you record each student as present (corrected sheet PDF + marks) or absent, and they see the result afterwards.'
              : 'This flow publishes one PDF question paper, gives you a shareable student exam link, and collects uploaded answer sheets inside logged-in student accounts.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div role="radiogroup" aria-label="How is this test conducted?" className="grid gap-3 sm:grid-cols-2">
              {[
                { value: 'online', title: 'Online exam', hint: 'Timed question PDF, students upload answers' },
                { value: 'offline', title: 'Paper test (offline)', hint: 'Written in class; you upload corrected sheets' },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`cursor-pointer rounded-lg border p-3 text-sm ${formData.conduct_mode === opt.value ? 'border-primary bg-primary/5 dark:border-accent-cool dark:bg-accent-cool/10' : 'border-slate-300 dark:border-slate-600'}`}
                >
                  <input
                    type="radio"
                    name="conduct_mode"
                    value={opt.value}
                    checked={formData.conduct_mode === opt.value}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <span className="block font-medium text-slate-900 dark:text-white">{opt.title}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{opt.hint}</span>
                </label>
              ))}
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Exam Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  placeholder="Monthly Test - Science"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Subject</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                >
                  <option value="" disabled>Select a subject…</option>
                  {subjects.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {offline ? (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Exam date</label>
                  <input
                    type="date"
                    name="exam_date"
                    value={formData.exam_date}
                    onChange={handleChange}
                    required
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  />
                </div>
              ) : (
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Duration (minutes)</label>
                <input
                  type="number"
                  min={1}
                  max={480}
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
              </div>
              )}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Total Marks</label>
                <input
                  type="number"
                  min={1}
                  name="total_marks"
                  value={formData.total_marks}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
              </div>
            </div>

            {!offline && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Question Paper PDF</label>
              <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center dark:border-slate-600">
                <input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={handlePdfSelect}
                />
                <label htmlFor="pdf-upload" className="cursor-pointer">
                  <Upload className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {pdfFile ? pdfFile.name : 'Click to choose the exam PDF'}
                  </p>
                  <p className="mt-1"><UploadHint /></p>
                </label>
              </div>
            </div>
            )}

            <div className="flex justify-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
              <button
                type="button"
                onClick={() => navigate('/faculty/exams')}
                className="rounded-md px-4 py-2 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-primary px-6 py-2.5 font-medium text-white transition-colors hover:bg-primary-light disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create Exam'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
